import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import isEqual from 'lodash/isEqual';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  forkJoin,
  from,
  Observable,
  ObservableInput,
  of,
  Subscription,
} from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { TranslationBundle, TranslationService } from './translation.service';
import { NotificationService } from './notification.service';
import { ChoiceDialog, DialogService } from './dialogs';

export type FormKey = string

export interface FormRegistration {
  control: AbstractControl
  reset: () => void
  save: () => ObservableInput<unknown> | void
  showNotifications?: boolean // suppresses notifications for all currently registered forms when set to false
  successMessageKey?: string
  errorMessageKey?: string
}

export interface FormStatus {
  dirty: boolean
  valid: boolean
}

export interface SaveFunction {
  (forms?: Map<FormKey, FormRegistration>): Observable<unknown>
}

interface Choice {
  value: string
  label: string
  testId: string
  color?: string
}

/**
 * When implementing ChangeManagerService in a new component, ensure the following:
 * 1. Add the `canDeactivate: [ChangeManagerGuard],` option to your route in the routing module.
 * 2. Inject the ChangeManagerService into your component, and call `registerForm` for each form you want to manage.
 *  fw-save-bar is the recommended way to save these forms.
 * 3. Don't forget to `deregisterForm` when the component is destroyed to prevent memory leaks.
 * 4. Currently there is an error when navigating from one page to another with the same form key (ex both pages use <info-edit>).
 * The old component gets destroyed after the new component is created, resulting in the old component deregistering the new instance
 * (the change manager stops detecting changes and guarding route) Still trying to think of a fix for the race condition.
 * Should we deregister forms automatically on route change?
 */

@Injectable({
  providedIn: 'root',
})
export class ChangeManagerService {
  readonly status$ = new BehaviorSubject<FormStatus>({ valid: false, dirty: false })

  private forms = new Map<FormKey, FormRegistration & { subscription: Subscription }>()
  private messages = {
    common: TranslationBundle.EMPTY,
    changeManager: TranslationBundle.EMPTY,
  }
  private subscriptions = {
    messages: Subscription.EMPTY,
    stateChange: Subscription.EMPTY,
  }

  constructor(
    private dialog: DialogService,
    private notifications: NotificationService,
    private translate: TranslationService,
  ) {
    this.subscriptions.messages = combineLatest([
      this.translate.getBundle('common'),
      this.translate.getBundle('fwChangeManager'),
    ]).subscribe(([common, changeManager]) => {
      this.messages = { common, changeManager };
    });
  }

  ngOnDestroy(): void {
    for (const { subscription } of this.forms.values()) {
      subscription.unsubscribe();
    }
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  private get entries(): FormRegistration[] {
    return Array.from(this.forms.values());
  }

  get controls(): AbstractControl[] {
    return this.entries.map(form => form.control);
  }

  // Having issues with status not responding to programatic changes?
  // I did too. I was adding and removing FormControls from a FormGroup but those changes weren't getting picked up.
  // The solution was to create the control, add it, and then set its value so the change manager will evaluate it as dirty
  // formGroup.addControl('control_name', addedGroupRoleControl);
  // formGroup.get('control_name').markAsDirty(); // very important to re-fetch the control
  // formGroup.get('control_name').setValue("some value"); // very important to re-fetch the control
  // removing is simliar, just set the control to an empty val
  // formGroup.get('control_name').markAsDirty();
  // formGroup.get('control_name').setValue('');
  // just make sure you aren't using an existing referenced to a control from a .get

  get valid(): boolean {
    return this.status$.value.valid;
  }

  get dirty(): boolean {
    return this.status$.value.dirty;
  }

  /**
   * Save the currently registered forms.
   * @param formKey optional key to save a specific form
   * @returns an observable that emits when the save is complete
   */
  save(formKey?: FormKey): Observable<unknown> {
    if (formKey) {
      const entry = this.forms.get(formKey);
      if (entry) {
        const result = entry.save();
        if (!result) {
          // if save returns undefined, we assume it was successful
          this.sampleFormStatus();
          return of(undefined);
        }
        return from(result).pipe(
          tap(() => {
            this.sampleFormStatus();
          }),
          catchError(error => {
            if (entry.showNotifications !== false) {
              this.notifications.error(entry.errorMessageKey);
            }
            return of(error);
          }),
        );
      }
    }
    const results = this.entries
      .filter(entry => entry.control.dirty)
      .map(entry => {
        const result = entry.save();
        return from(result ? result : of(undefined)).pipe(
          map(saveResult => ({ entry, result: saveResult, error: null })),
          catchError(error => {
            return of({ entry, result: null, error }); // Include the entry in case of an error
          }),
        );
      });
    return forkJoin(results).pipe(
      tap(results => {
        for (const { entry, error } of results) {
          if (error) {
            if (entry.showNotifications) {
              this.notifications.error(entry.errorMessageKey);
            }
            return;
          }
          if (entry.showNotifications) {
            this.notifications.success(entry.successMessageKey);
          }
        }
        this.sampleFormStatus();
      }),
      map(results => results.map(result => result?.result)),
      catchError(error => {
        // constituent components should log specific errors before we get to this point
        return of(error);
      }),
    );
  }

  reset(): void {
    for (const entry of this.entries) {
      entry.reset();
    }
    this.sampleFormStatus();
  }

  /**
   * Force the user to save or discard pending changes.
   * @returns true to proceed with navigation, or false to cancel
   */
  promptDecision(): Observable<boolean> {
    const choices = [
      { value: 'discard', label: this.messages.common.get('actions.discardChanges'), testId: 'discard-button' },
    ] as Choice[];
    if (this.valid) {
      choices.push({ value: 'save', label: this.messages.common.get('actions.save'), testId: 'save-button', color: 'primary' });
    }
    return this.dialog.open(ChoiceDialog, {
      data: {
        title: this.messages.changeManager.get(this.valid ? 'title' : 'invalidTitle'),
        content: this.messages.changeManager.get(this.valid ? 'content' : 'invalidContent'),
        choices,
        closeValue: null,
        alignActions: 'end',
      },
    }).afterClosed().pipe(
      switchMap((choice: 'discard' | 'save' | null) => {
        if (choice === 'save') {
          return this.save().pipe(map(() => true));
        }
        if (choice === 'discard') {
          this.reset();
          return of(true);
        }
        return of(false);
      }),
    );
  }

  /**
   * Register a form as part of the change manager, preventing route changes
   * until the form is saved or reset.
   * @param key the key to identify the form
   * @param registration
   */
  registerForm(key: FormKey, registration: FormRegistration): void {
    if (this.forms.has(key)) {
      this.deregisterForm(key);
    }
    if (registration.showNotifications === undefined) {
      registration.showNotifications = true;
    }
    if (registration.successMessageKey === undefined) {
      registration.successMessageKey = 'common.save.success';
    }
    if (registration.errorMessageKey === undefined) {
      registration.errorMessageKey = 'common.save.error';
    }
    const subscription = registration.control.statusChanges.subscribe(() => {
      this.sampleFormStatus();
    });
    this.forms.set(key, { ...registration, subscription });
  }

  deregisterForm(key: FormKey): void {
    const entry = this.forms.get(key);
    if (entry) {
      entry.subscription.unsubscribe();
    }
    this.forms.delete(key);
    this.sampleFormStatus();
  }

  private sampleFormStatus(): void {
    const controls = this.controls;
    const status = {
      dirty: controls.some(control => control.dirty),
      valid: controls.every(control => control.valid),
    };
    if (!isEqual(status, this.status$.value)) {
      this.status$.next(status);
    }
  }
}
